import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { UserContext } from '../ContextProviders';
import { useNovaChat } from '@/hooks/useNovaChat';
import { applyNovaCitations, buildNovaRagMetadataFilter, buildNovaReport } from '@/api';
import { NovaChatMessage } from '@/types';
import { useNova } from './NovaContext';
import { NovaTaxonomy, NovaTaxonomyOption, emptyNovaTaxonomy } from './novaTaxonomy';
import NovaLandingSidebar from './NovaLandingSidebar';
import { NovaSingleSelect, NovaTagMultiSelect, NovaTextField } from './NovaTemplateFilters';
import NovaAnswerCard from './NovaAnswerCard';
import { novaThemes, novaDisclaimer } from './novaTheme';
import { NovaChevronRightIcon } from './novaIcons';

const NOVA_HOME_HREF = '/tools/nova-ai';

interface TemplateSingleFilter {
  key: string;
  kind: 'single';
  label: string;
  hint?: string;
  options: NovaTaxonomyOption[];
  required?: boolean;
  metadataKey?: string;
}

interface TemplateMultiFilter {
  key: string;
  kind: 'multi';
  label: string;
  hint?: string;
  suggestions: NovaTaxonomyOption[];
  allowFreeText?: boolean;
  required?: boolean;
  metadataKey?: string;
}

interface TemplateTextFilter {
  key: string;
  kind: 'text';
  label: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  metadataKey?: undefined;
}

type TemplateFilter = TemplateSingleFilter | TemplateMultiFilter | TemplateTextFilter;
type TemplateFilterValues = Record<string, string | string[]>;

interface ReportTemplateDetail {
  headline: string;
  standfirst: string;
  filters: TemplateFilter[];
  buildPrompt: (values: TemplateFilterValues) => string;
  sourceMonths?: number;
  fixedMetadata?: Record<string, string[]>;
}

interface ReportTemplate {
  title: string;
  desc: string;
  prompt: string;
  // Templates without `detail` still seed Nova chat directly from `prompt` via
  // "Use" — only templates with a finalised filter set get the configure step.
  detail?: ReportTemplateDetail;
}

const REPORT_WRITING_RULES = [
  'Writing rules:',
  '- Write in the Stylus Style Guide voice, with confidence.',
  '- Use only the supplied source material.',
  '- Do not add scope, source or caveat notes, and do not mention missing data, publication dates, full reports or images.',
  '- Only include statistics that appear in the source material. If fewer are available than requested, include just those — no placeholders and no comment on the shortfall.',
  '- Do not describe, recommend or reference images; they are added separately.',
];

const buildCaseStudiesPrompt = (values: TemplateFilterValues): string => {
  const topics = (values.topics as string[]) || [];
  const focus = (values.focus as string) || '';
  const regions = (values.regions as string[]) || [];

  const lines = [
    'Create a Best-in-Class Case Studies report.',
    '',
    'Source: all Stylus reports from the last 12 months, prioritising the most recent content.',
    '',
    'Filters:',
    `- Topics: ${topics.join(', ')}`,
  ];
  if (focus) lines.push(`- Focus: ${focus}`);
  if (regions.length) lines.push(`- Regions: ${regions.join(', ')}`);
  lines.push(
    '',
    'Sections:',
    '1. Executive Summary',
    '2. Case studies — up to 20 best-practice brand projects and key initiatives matching the filters above, pulled from across Stylus reports',
    '3. Future Outlook',
    '',
    ...REPORT_WRITING_RULES,
  );
  return lines.join('\n');
};

const buildEventRoundupPrompt = (values: TemplateFilterValues): string => {
  const topic = (values.topic as string) || '';

  return [
    'Create an Event Round-up briefing.',
    '',
    'Source: all event reports — trade show, conference and festival coverage — from the last 12 months, prioritising the most recent content.',
    '',
    'Filters:',
    `- Topic: ${topic}`,
    '',
    'Sections:',
    '1. Executive Summary — 50 words maximum',
    '2. Key themes — the trended themes across this coverage (e.g. Sustainable Innovation, Carnival Beauty)',
    '3. Product/Activation examples',
    '4. Future Outlook',
    '',
    ...REPORT_WRITING_RULES,
  ].join('\n');
};

const eventRoundupDetail = (taxonomy: NovaTaxonomy): ReportTemplateDetail => ({
  headline: 'Event round-up',
  standfirst: 'Trade show, conference and festival coverage, summarised into one topic-specific briefing.',
  filters: [
    {
      key: 'topic',
      kind: 'single',
      label: 'Topic',
      hint: 'single',
      options: taxonomy.topics,
      required: true,
      metadataKey: 'Topics',
    },
  ],
  buildPrompt: buildEventRoundupPrompt,
  fixedMetadata: { 'Entities.report_types': ['Event Coverage'] },
});

const caseStudiesDetail = (taxonomy: NovaTaxonomy): ReportTemplateDetail => ({
  headline: 'Best-in-class case studies',
  standfirst:
    'Best-practice brand projects and key initiatives, pulled from across Stylus reports and populated into one report.',
  filters: [
    {
      key: 'topics',
      kind: 'multi',
      label: 'Topic',
      hint: 'multiple',
      suggestions: taxonomy.topics,
      required: true,
      metadataKey: 'Topics',
    },
    {
      key: 'focus',
      kind: 'text',
      label: 'Narrow down',
      hint: 'free text',
      placeholder: 'e.g. Refillable packaging',
    },
    {
      key: 'regions',
      kind: 'multi',
      label: 'Region',
      hint: 'multiple',
      suggestions: taxonomy.regions,
      metadataKey: 'Regions',
    },
  ],
  buildPrompt: buildCaseStudiesPrompt,
});

const buildCrossIndustryPrompt = (values: TemplateFilterValues): string => {
  const trend = (values.trend as string) || '';

  return [
    'Create a Cross Industry Report.',
    '',
    'Source: all reports from the last 12 months, prioritising the most recent content.',
    '',
    'Filters:',
    `- Trend or topic: ${trend}`,
    '',
    'Sections:',
    '1. Executive Summary',
    '2. Key stats — up to 5 of the most compelling statistics',
    '3. Consumer & Lifestyle Trends',
    '4. Product Trends',
    '5. Engagement Trends',
    '6. Future Outlook',
    '',
    ...REPORT_WRITING_RULES,
  ].join('\n');
};

const crossIndustryDetail = (): ReportTemplateDetail => ({
  headline: 'Cross-industry report',
  standfirst: 'A cross-industry lens on a chosen trend or topic, drawn from across Stylus reports.',
  filters: [
    {
      key: 'trend',
      kind: 'text',
      label: 'Trend or topic',
      hint: 'free text',
      placeholder: 'e.g. Premiumisation',
      required: true,
    },
  ],
  buildPrompt: buildCrossIndustryPrompt,
});

const buildGenerationReportPrompt = (values: TemplateFilterValues): string => {
  const generation = (values.generation as string) || '';
  const regions = (values.regions as string[]) || [];

  const lines = [
    'Create a Generation Report.',
    '',
    'Source: all reports from the last 18 months, prioritising the most recent content.',
    '',
    'Filters:',
    `- Generation: ${generation}`,
  ];
  if (regions.length) lines.push(`- Regions: ${regions.join(', ')}`);
  lines.push(
    '',
    'Sections:',
    '1. Executive Summary',
    '2. Key stats — up to 5 of the most compelling statistics',
    '3. Consumer & Lifestyle — mindsets, attitudes and social/lifestyle behaviours',
    '4. Product — Fashion, Beauty, Food & Drink, Digital and other categories',
    '5. Retail & Brand Comms — retail, marketing & brand comms, pop culture & media',
    '6. Future Outlook',
    '',
    ...REPORT_WRITING_RULES,
  );
  return lines.join('\n');
};

const generationReportDetail = (taxonomy: NovaTaxonomy): ReportTemplateDetail => ({
  headline: 'Generation report',
  standfirst: 'The mood of a chosen generation, and what it means across industries, drawn from across Stylus reports.',
  filters: [
    {
      key: 'generation',
      kind: 'single',
      label: 'Generation',
      hint: 'single',
      options: taxonomy.generations,
      required: true,
      metadataKey: 'Generations',
    },
    {
      key: 'regions',
      kind: 'multi',
      label: 'Region',
      hint: 'multiple',
      suggestions: taxonomy.regions,
      metadataKey: 'Regions',
    },
  ],
  buildPrompt: buildGenerationReportPrompt,
  sourceMonths: 18,
});

// Analyst-verified starter prompts — copy is representative per the design
// handoff, not final; product/analyst teams own the eventual wording. Filter
// options come from the CMS /tools/nova-ai taxonomy (see novaTaxonomy.ts).
const buildReportTemplates = (taxonomy: NovaTaxonomy): ReportTemplate[] => [
  {
    title: 'Event Round Up',
    desc: 'Summarising trade show, conference and festival coverage into a topic-specific briefing.',
    prompt:
      'Give me an event round-up briefing that summarises the latest trade show, conference and festival coverage for a chosen topic.',
    detail: eventRoundupDetail(taxonomy),
  },
  {
    title: 'Cross Industry Report',
    desc: 'Demonstrating a cross-industry lens on a chosen trend or topic.',
    prompt:
      'Build a cross-industry report showing how a chosen trend or topic is playing out across multiple industries.',
    detail: crossIndustryDetail(),
  },
  {
    title: 'Best in Class Case Studies Report',
    desc: 'Find and populate best-practice examples of relevant brand projects and key initiatives from across Stylus reports.',
    prompt:
      'Find and summarise best-in-class case studies — standout brand projects and initiatives — for a chosen topic.',
    detail: caseStudiesDetail(taxonomy),
  },
  {
    title: 'Generation Report',
    desc: 'Capturing the mood of the generation and what it means across industries.',
    prompt: 'Build a generation report capturing the mood of a chosen generation and what it means across industries.',
    detail: generationReportDetail(taxonomy),
  },
];

const initialFilterValues = (filters: TemplateFilter[]): TemplateFilterValues =>
  filters.reduce<TemplateFilterValues>((acc, filter) => {
    acc[filter.key] = filter.kind === 'multi' ? [] : '';
    return acc;
  }, {});

interface NovaTemplatesProps {
  taxonomy?: NovaTaxonomy | null;
}

// The "Build a Report" full-page state from the Nova buildout handoff (Nova Page
// Templates). Reuses the same rail as NovaLanding so the two full-page states read
// as one product; "Use" hands the template's prompt to a fresh Nova conversation
// via NovaLanding's ?q= seed rather than duplicating chat/streaming logic here.
// Every template now has a finalised filter set and opens a configure step
// first, so the filters get folded into the seeded prompt instead of being lost.
const NovaTemplates: React.FC<NovaTemplatesProps> = ({ taxonomy }) => {
  const router = useRouter();
  const user = useContext(UserContext);
  const { theme } = useNova();
  const { conversations, conversationsLoading, loadConversations, deleteConversations } = useNovaChat();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<ReportTemplate | null>(null);
  const [filterValues, setFilterValues] = useState<TemplateFilterValues>({});
  const [reportMessage, setReportMessage] = useState<NovaChatMessage | null>(null);
  const [reportWaiting, setReportWaiting] = useState(false);
  const [reportStreaming, setReportStreaming] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const surfaces = novaThemes[theme];
  const reportTemplates = useMemo(() => buildReportTemplates(taxonomy ?? emptyNovaTaxonomy()), [taxonomy]);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUseTemplate = (prompt: string) => {
    router.push(`${NOVA_HOME_HREF}?q=${encodeURIComponent(prompt)}`);
  };

  const openTemplate = (template: ReportTemplate) => {
    if (!template.detail) {
      handleUseTemplate(template.prompt);
      return;
    }
    setFilterValues(initialFilterValues(template.detail.filters));
    setReportMessage(null);
    setReportError(null);
    setActiveTemplate(template);
  };

  const closeTemplate = () => {
    setActiveTemplate(null);
    setReportMessage(null);
    setReportError(null);
  };

  const setFilterValue = (key: string, value: string | string[]) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const canCreateReport = Boolean(
    activeTemplate?.detail?.filters.every((filter) => {
      if (!filter.required) return true;
      const value = filterValues[filter.key];
      return filter.kind === 'multi' ? Array.isArray(value) && value.length > 0 : Boolean(value);
    }),
  );

  // "Build a Report" retrieves source chunks from the multimodal RAG /search API and
  // has its /reports/generate endpoint write the report from them (api/nova-ava.ts),
  // rendering the result inline below the form via the same NovaAnswerCard used on the chat page.
  const handleCreateReport = async () => {
    if (!activeTemplate?.detail || !canCreateReport) return;
    const prompt = activeTemplate.detail.buildPrompt(filterValues);
    const selectedValues = (filter: TemplateFilter) =>
      ([] as string[])
        .concat(filterValues[filter.key] ?? [])
        .map((value) => value.trim())
        .filter(Boolean);

    const metadataFields = activeTemplate.detail.filters.reduce<Record<string, string[]>>((acc, filter) => {
      const values = filter.kind === 'text' ? [] : selectedValues(filter);
      if (filter.metadataKey && values.length) acc[filter.metadataKey] = values;
      return acc;
    }, { ...activeTemplate.detail.fixedMetadata });
    const searchQuery = activeTemplate.detail.filters.flatMap(selectedValues).join(', ') || ' ';
    const metadataFilter = buildNovaRagMetadataFilter(metadataFields, activeTemplate.detail.sourceMonths);

    if (!user?.amsUserId) {
      setReportError("Nova can't identify your account right now, so it can't create this report. Please contact support.");
      return;
    }

    setReportError(null);
    setReportMessage(null);
    setReportWaiting(true);
    setReportStreaming(true);

    try {
      const report = await buildNovaReport({
        prompt,
        searchQuery,
        metadataFilter,
        userProfileId: user.amsUserId,
      });
      const resolved = applyNovaCitations(report.citations, report.images);
      setReportMessage({
        role: 'assistant',
        content: report.content,
        sources: resolved.sources,
        images: resolved.images,
        timestamp: Date.now(),
      });
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Something went wrong creating this report.');
    } finally {
      setReportWaiting(false);
      setReportStreaming(false);
    }
  };

  return (
    <div
      className="nova-full-page flex h-[calc(100dvh-var(--header-height))] flex-col gap-0 overflow-hidden md:flex-row"
      style={{ background: surfaces.surfaces.panelBase, color: surfaces.ink.body }}
    >
      <NovaLandingSidebar
        theme={theme}
        active="templates"
        onNavClick={(key) => key === 'templates' && closeTemplate()}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(true)}
        onExpand={() => setSidebarCollapsed(false)}
        onNewChat={() => router.push(NOVA_HOME_HREF)}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        onSelectConversation={(id) => router.push(`${NOVA_HOME_HREF}?c=${encodeURIComponent(id)}`)}
        onDeleteConversation={(id) => deleteConversations([id])}
        userName={user?.name}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-y-auto" data-lenis-prevent>
          <div className="mx-auto max-w-[1040px] px-4 pt-6 pb-8 sm:px-8 md:px-12 md:pt-11 md:pb-10">
            {activeTemplate?.detail ? (
              <>
                <div
                  className="mb-2.5 flex items-center gap-1.5 text-xs tracking-[0.06em] uppercase"
                  style={{ color: surfaces.ink.secondary }}
                >
                  <button
                    type="button"
                    onClick={closeTemplate}
                    className="cursor-pointer border-none bg-transparent p-0 uppercase"
                    style={{ color: surfaces.ink.secondary }}
                  >
                    Build a report
                  </button>
                  <NovaChevronRightIcon className="h-3 w-3" />
                  <span style={{ color: surfaces.ink.placeholder }}>{activeTemplate.title}</span>
                </div>
                <h1
                  className="font-sohne mb-3 max-w-[900px] text-[28px] leading-[32px] font-semibold tracking-[-0.6px] md:text-[40px] md:leading-[44px] md:tracking-[-1.1px] text-pretty"
                  style={{ color: surfaces.ink.heading }}
                >
                  {activeTemplate.detail.headline}
                </h1>
                <p
                  className="mb-6 max-w-[620px] text-base leading-[24px] text-pretty md:mb-[30px] md:text-lg md:leading-[27px]"
                  style={{ color: surfaces.ink.body }}
                >
                  {activeTemplate.detail.standfirst}
                </p>

                <div
                  className="rounded-2xl border p-4 md:p-6"
                  style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {activeTemplate.detail.filters.map((filter) => {
                      if (filter.kind === 'single') {
                        return (
                          <NovaSingleSelect
                            key={filter.key}
                            theme={theme}
                            label={filter.label}
                            hint={filter.hint}
                            options={filter.options}
                            value={(filterValues[filter.key] as string) || ''}
                            onChange={(value) => setFilterValue(filter.key, value)}
                          />
                        );
                      }
                      if (filter.kind === 'text') {
                        return (
                          <NovaTextField
                            key={filter.key}
                            theme={theme}
                            label={filter.label}
                            hint={filter.hint}
                            placeholder={filter.placeholder}
                            value={(filterValues[filter.key] as string) || ''}
                            onChange={(value) => setFilterValue(filter.key, value)}
                            onSubmit={() => {
                              if (!reportWaiting && !reportStreaming) handleCreateReport();
                            }}
                          />
                        );
                      }
                      return (
                        <NovaTagMultiSelect
                          key={filter.key}
                          theme={theme}
                          label={filter.label}
                          hint={filter.hint}
                          suggestions={filter.suggestions}
                          allowFreeText={filter.allowFreeText}
                          values={(filterValues[filter.key] as string[]) || []}
                          onChange={(values) => setFilterValue(filter.key, values)}
                        />
                      );
                    })}
                  </div>

                  <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleCreateReport}
                      disabled={!canCreateReport || reportWaiting || reportStreaming}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
                    >
                      {reportWaiting || reportStreaming ? 'Creating report…' : 'Create report'}
                      <NovaChevronRightIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {reportError && (
                  <div
                    className="mt-6 rounded-2xl border p-4 text-sm md:p-6"
                    style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.body }}
                  >
                    {reportError}
                  </div>
                )}

                {(reportWaiting || reportStreaming || reportMessage) && !reportError && (
                  <div
                    className="mt-6 rounded-2xl border p-4 md:p-6"
                    style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
                  >
                    <NovaAnswerCard
                      message={reportMessage ?? undefined}
                      waiting={reportWaiting}
                      streaming={reportStreaming}
                      theme={theme}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mb-2.5 text-xs tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
                  Build a report
                </div>
                <h1
                  className="font-sohne mb-3 max-w-[900px] text-[28px] leading-[32px] font-semibold tracking-[-0.6px] md:text-[40px] md:leading-[44px] md:tracking-[-1.1px] text-pretty"
                  style={{ color: surfaces.ink.heading }}
                >
                  Analyst-verified prompts
                </h1>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {reportTemplates.map((template) => (
                    <div
                      key={template.title}
                      role="button"
                      tabIndex={0}
                      onClick={() => openTemplate(template)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openTemplate(template);
                        }
                      }}
                      className="flex min-h-[150px] cursor-pointer flex-col rounded-2xl border p-5 md:min-h-[180px] md:p-[26px]"
                      style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
                    >
                      <h3
                        className="mb-2 text-[19px] leading-6 font-semibold tracking-[-0.2px]"
                        style={{ color: surfaces.ink.heading }}
                      >
                        {template.title}
                      </h3>
                      <p className="text-[15px] leading-[22px]" style={{ color: surfaces.ink.secondary }}>
                        {template.desc}
                      </p>
                      <span className="flex-1" />
                      <div className="mt-[18px] flex items-center justify-end">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openTemplate(template);
                          }}
                          className="inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[13.5px] font-semibold"
                          style={{ color: surfaces.ink.heading }}
                        >
                          Use
                          <NovaChevronRightIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div
          className="shrink-0 px-4 pt-2 pb-[18px] text-center text-xs md:text-sm"
          style={{ color: surfaces.ink.placeholder }}
        >
          {novaDisclaimer.short}
        </div>
      </div>
    </div>
  );
};

export default NovaTemplates;
